import { APITypesV1 } from "@cur8/api-client";
import { Button, Icon24ArrowRight, Typography } from "@cur8/maneki";
import { useAssignToEcgMutation } from "hooks/mutations/useAssignToEcgMutation";
import { useClearRoomControllerMutation } from "hooks/mutations/useClearRoomControllerMutation";
import { useBloodWorkModeQuery } from "hooks/queries/useBloodWorkModeQuery";
import { useECGDeviceQuery } from "hooks/queries/useECGDeviceQuery";
import { assignErrorParam, isEcgDeviceOffline } from "lib/isEcgDeviceOffline";
import { useLabStatusBloodSamplesQuery } from "hooks/queries/useLabStatusBloodSamplesQuery";
import { useVisitBloodSamplesQuery } from "hooks/queries/useVisitBloodSamplesQuery";
import { useVisitQuery } from "hooks/queries/useVisitQuery";
import { useShowNekoLens } from "hooks/useShowNekoLens";
import { isReassignNoravEcg } from "lib/isReassignNoravEcg";
import { ecgScanLabel } from "lib/ecgScanLabel";
import { readScanStateFromVisit } from "lib/readScanStateFromVisit";
import { drawProgress, hasLimsManagedSamples, tubePills } from "lib/tubeStatus";
import { CheckInParams } from "lib/types/checkInParameters";
import { Fragment, ReactNode, useCallback, useMemo } from "react";
import { useFeaturePreference } from "render/hooks/useFeaturePreference";
import { paths } from "render/routes/paths";
import { InformationLayout } from "render/ui/layout/InformationLayout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent/PageFrameContent";
import { List } from "render/ui/presentation/List";
import {
  StatusBadge,
  StatusBadgeHighlight,
} from "render/ui/presentation/StatusBadge";
import { Link } from "render/ui/trigger/Link";
import {
  bloodWorkStatus,
  BloodWorkStatus,
} from "render/views/AssignVials/scanDecision";
import { useCardioSecurDeeplink } from "render/views/CheckInCompleteView/hooks/useCardioSecurDeeplink";
import styles from "./styles.module.sass";
type CheckInCompleteViewProps = {
  goTo(url: string): void;
} & CheckInParams;

export function CheckInCompleteView({
  goTo,
  patientId,
  visitId,
}: CheckInCompleteViewProps) {
  const { mutate: clearRoomController } = useClearRoomControllerMutation();
  const { data: visit } = useVisitQuery({
    patientId,
    visitId,
    options: { refetchInterval: 5000 },
  });
  const showNekoLens = useShowNekoLens({ patientId, visitId });
  const skinScanState = readScanStateFromVisit("skin", visit);
  const cardioScanState = readScanStateFromVisit("cardio", visit);
  const tissueScanState = readScanStateFromVisit("tci", visit);
  const ecgScanState = readScanStateFromVisit("ecg", visit);

  const { data: bloodWorkModeData } = useBloodWorkModeQuery({
    patientId,
    visitId,
  });
  const bloodWorkMode = bloodWorkModeData?.bloodWorkMode;
  const { data: featurePreferences } = useFeaturePreference();
  // Polls while the flag is on: a lab rejection has to reach the operator while
  // the member is still in the clinic (AB#92441).
  const { labStatusEnabled, query: bloodSamplesQuery } =
    useLabStatusBloodSamplesQuery({ patientId, visitId });
  // The samples check is the only gate (AB#93176): it scopes the tile to
  // Sangra/LIMS sites, which is what the flow needs. The per-clinic toggle
  // that used to sit on top of it is gone, the flow ships globally.
  const showDrawConfirmation = hasLimsManagedSamples(
    bloodSamplesQuery.data?.bloodSamples
  );

  // With the toggle on, checkout is confirmed on its own screen (AB#92438) and
  // the clear-room-controller call runs there instead. With it off this stays
  // the old one-press checkout, so the two paths never both fire the mutation.
  //
  // The finish-the-scan wording belongs to the confirmed flow (AB#102209). With
  // the toggle off this button still ends the visit on the first press, so it
  // keeps the old label rather than promising a confirmation that never comes.
  const checkoutFlowEnabled = featurePreferences.checkoutFlowEnabled;
  const goToNext = useCallback(() => {
    if (featurePreferences.checkoutFlowEnabled) {
      goTo(paths.checkOutConfirm.url({ patientId, visitId }));
      return;
    }
    clearRoomController(
      { patientId, visitId },
      { onSuccess: () => goTo(paths.root.url({})) }
    );
  }, [
    clearRoomController,
    featurePreferences.checkoutFlowEnabled,
    goTo,
    patientId,
    visitId,
  ]);

  const goToNekoLensAuth = useCallback(() => {
    const url = paths.nekoLensAuth.url({ patientId, visitId });
    goTo(url);
  }, [goTo, patientId, visitId]);

  const skinScanTile = (
    <ScanTile
      title="Skin Scan"
      scanState={skinScanState}
      action={
        <Link
          onPress={() =>
            goTo(paths.assignToSkinScanner.url({ patientId, visitId }))
          }
        >
          Re-assign member
        </Link>
      }
    />
  );

  const drawConfirmationTile = showDrawConfirmation ? (
    <DrawConfirmationListItem
      goTo={goTo}
      patientId={patientId}
      visitId={visitId}
      samples={bloodSamplesQuery.data?.bloodSamples}
      labStatusEnabled={labStatusEnabled}
    />
  ) : undefined;

  const ecgTile = (
    <ECGListItem
      goTo={goTo}
      ecgScanState={ecgScanState}
      patientId={patientId}
      visitId={visitId}
    />
  );

  const cardioScanTile = (
    <ScanTile title="Cardio Scan" scanState={cardioScanState} />
  );

  const tissueScanTile = (
    <ScanTile title="Tissue Scan" scanState={tissueScanState} />
  );

  const bloodWorkTile =
    bloodWorkMode === APITypesV1.BloodWorkMode.BarcodeAssignment ? (
      <BloodWorkListItem goTo={goTo} patientId={patientId} visitId={visitId} />
    ) : undefined;

  // Neko Lens loads the visit's derma session, which the backend creates from
  // the skin scan, a moment after the scan appears on the visit. Waiting for
  // the scan to finish leaves no gap where the device finds no session, and
  // keeps the code away from a failed scan, which has nothing to work on. The
  // row stays up meanwhile and says what it is waiting for, so it doesn't read
  // as Neko Lens being unavailable here. Not before the visit has loaded, when
  // there is no skin scan state to wait on yet.
  const dermaTile =
    showNekoLens && visit != null ? (
      <ScanTile
        title="Derma"
        action={
          <div className={styles.itemActions}>
            {skinScanState === APITypesV1.ImmutableScanState.Finished ? (
              <Link onPress={goToNekoLensAuth}>Show QR code</Link>
            ) : (
              <Typography variant="body-m" color="subtle">
                {skinScanState === APITypesV1.ImmutableScanState.Failed
                  ? "Needs a completed skin scan"
                  : "Available once the skin scan is complete"}
              </Typography>
            )}
          </div>
        }
      />
    ) : undefined;

  // Order as data. A divider belongs to the gap between two tiles, not to a
  // tile, so the list interleaves them and no tile is a special case.
  const drawConfirmation = {
    id: "draw-confirmation",
    node: drawConfirmationTile,
  };
  const tissueScan = { id: "tissue-scan", node: tissueScanTile };

  const [afterSkinScan, lastScan] = featurePreferences.bloodDrawStepEnabled
    ? [drawConfirmation, tissueScan]
    : [tissueScan, drawConfirmation];

  const tiles = [
    { id: "skin-scan", node: skinScanTile },
    afterSkinScan,
    { id: "ecg", node: ecgTile },
    { id: "cardio-scan", node: cardioScanTile },
    lastScan,
    // Only on some visits, so they go last rather than interrupting the scans.
    { id: "blood-work", node: bloodWorkTile },
    { id: "derma", node: dermaTile },
  ].filter((tile) => tile.node != null);

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout}>
            <Typography variant="display-s">Scan progress</Typography>
            <List>
              {/* Keyed by tile, not position: the draw confirmation tile
                  appears when its samples query resolves, and index keys would
                  remount every tile below it, including the ECG tile
                  mid-assignment, which drops its mutation callbacks. */}
              {tiles.map(({ id, node }, index) => (
                <Fragment key={id}>
                  {index > 0 ? <List.Divider /> : undefined}
                  <List.Item>{node}</List.Item>
                </Fragment>
              ))}
            </List>
            <div className={styles.divider} />
            <section className={styles.checkoutSection}>
              <div className={styles.checkoutText}>
                <Typography variant="title-l">
                  {checkoutFlowEnabled
                    ? "Ready to finish the scan?"
                    : "Ready to check out?"}
                </Typography>
                <Typography variant="body-m" color="subtle">
                  {checkoutFlowEnabled
                    ? "Finish the scan once all examinations are complete"
                    : "Check out if all examinations are complete"}
                </Typography>
              </div>
              <Button
                onPress={goToNext}
                fillContainer
                icon={checkoutFlowEnabled ? undefined : <Icon24ArrowRight />}
              >
                {checkoutFlowEnabled ? "Finish the scan" : "Check Out"}
              </Button>
            </section>
          </section>
        }
      />
    </PageFrameContent>
  );
}

type ScanStatusBadgeProps = {
  scanState: APITypesV1.ImmutableScanState | undefined;
};

function ECGListItem({
  patientId,
  ecgScanState,
  visitId,
  goTo,
}: {
  visitId: string;
  patientId: string;
  ecgScanState: APITypesV1.ImmutableScanState | undefined;
  goTo(url: string): void;
}) {
  const eCGDeviceQuery = useECGDeviceQuery({ patientId, visitId });
  const assignToEcgMutation = useAssignToEcgMutation();
  const openCardioSecurApp = useCardioSecurDeeplink({
    patientId,
    visitId,
  });

  const noravAssignmentStatus =
    eCGDeviceQuery.data?.deviceType === APITypesV1.EcgType.EcgNorav
      ? eCGDeviceQuery.data?.assignmentStatus
      : undefined;

  const handleNoravPress = useCallback(() => {
    if (noravAssignmentStatus === APITypesV1.DeviceAssignmentStatus.CanAssign) {
      if (assignToEcgMutation.isPending) {
        return;
      }
      assignToEcgMutation.mutate(
        { patientId, visitId },
        {
          onSuccess: () => {
            eCGDeviceQuery.refetch();
          },
          onError: (error) => {
            goTo(
              paths.assignToNoravECGDevice.url({ patientId, visitId }) +
                `?assignError=${assignErrorParam(error)}`
            );
          },
        }
      );
      return;
    }
    goTo(paths.assignToNoravECGDevice.url({ patientId, visitId }));
  }, [
    assignToEcgMutation,
    eCGDeviceQuery,
    goTo,
    noravAssignmentStatus,
    patientId,
    visitId,
  ]);

  const data = useMemo(() => {
    if (eCGDeviceQuery.error) {
      return {
        label: isEcgDeviceOffline(eCGDeviceQuery.error)
          ? "ECG device offline (try again?)"
          : "Could not fetch ECG device (try again?)",
        onClick: () => {
          if (eCGDeviceQuery.isFetching) {
            return;
          }
          const refetch = eCGDeviceQuery.refetch;
          refetch();
        },
      };
    }
    switch (eCGDeviceQuery.data?.deviceType) {
      case APITypesV1.EcgType.EcgCardiosecur:
        return {
          label:
            ecgScanState === APITypesV1.ImmutableScanState.Finished
              ? "Re-assign member"
              : "Assign to CardioSecur",
          onClick: () => openCardioSecurApp(),
        };
      case APITypesV1.EcgType.EcgNorav: {
        const isReassign = isReassignNoravEcg(
          noravAssignmentStatus,
          ecgScanState
        );
        return {
          label: isReassign ? "Re-assign member" : "Assign to Norav",
          onClick: handleNoravPress,
        };
      }
      default:
        return undefined;
    }
  }, [
    eCGDeviceQuery.isFetching,
    eCGDeviceQuery.refetch,
    eCGDeviceQuery.error,
    eCGDeviceQuery.data,
    ecgScanState,
    handleNoravPress,
    noravAssignmentStatus,
    openCardioSecurApp,
  ]);

  return (
    <div
      data-testid="check-in-complete-ecg-section"
      className={styles.scanItem}
    >
      <div className={styles.itemTitle}>
        <Typography variant="title-s">
          {ecgScanLabel(eCGDeviceQuery.data?.deviceType)}
        </Typography>
        <div className={styles.itemActions}>
          <Link onPress={data?.onClick}>{data?.label}</Link>
          <Typography variant="body-m" color="subtle">
            ({patientId})
          </Typography>
        </div>
      </div>
      {ecgScanState != null ? (
        <ScanStatusBadge scanState={ecgScanState} />
      ) : undefined}
    </div>
  );
}

function DrawConfirmationListItem({
  goTo,
  patientId,
  visitId,
  samples,
  labStatusEnabled,
}: {
  goTo(url: string): void;
  patientId: string;
  visitId: string;
  samples: APITypesV1.BloodSample[] | undefined;
  labStatusEnabled: boolean;
}) {
  const { data: featurePreferences } = useFeaturePreference();
  const { recorded, total } = drawProgress(samples);
  const allResolved = total > 0 && recorded === total;
  const pills = tubePills(samples);

  const goToDrawConfirmation = useCallback(() => {
    goTo(paths.drawConfirmation.url({ patientId, visitId }));
  }, [goTo, patientId, visitId]);

  // Per tube, not for the pair: one tube can be at the lab while the operator
  // gets help with the next, and a half-done step is neither Completed nor as
  // untouched as Missing reads (AB#91601). Behind the flag with the rest of the
  // flow: without a way to send one tube, a partial count can't be reached.
  // Only reached with lab status off, which owns this slot when it is on and
  // says more about each tube than a count of confirmations would.
  const partial =
    featurePreferences.singleVialConfirmEnabled && recorded > 0 && !allResolved;
  const badge = allResolved
    ? { label: "Completed", highlight: "success" as const }
    : partial
      ? {
          label: `${recorded} of ${total} confirmed`,
          highlight: "warning" as const,
        }
      : { label: "Missing", highlight: "error" as const };

  return (
    <div className={styles.scanItem}>
      <div className={styles.itemTitle}>
        <Typography variant="title-s">Blood draw confirmation</Typography>
        <Link onPress={goToDrawConfirmation}>
          {allResolved ? "Review tubes" : "Confirm tubes"}
        </Link>
      </div>
      {labStatusEnabled ? (
        <div
          className={styles.statusBadges}
          data-testid="draw-confirmation-status"
        >
          {pills.map((pill) => (
            <StatusBadge
              key={pill.key}
              label={pill.label}
              highlight={pill.highlight}
              pending={pill.pending}
            />
          ))}
        </div>
      ) : (
        <StatusBadge label={badge.label} highlight={badge.highlight} />
      )}
    </div>
  );
}

function BloodWorkListItem({
  goTo,
  patientId,
  visitId,
}: {
  goTo(url: string): void;
  patientId: string;
  visitId: string;
}) {
  const bloodSamplesQuery = useVisitBloodSamplesQuery({ patientId, visitId });
  const samples = bloodSamplesQuery.data?.bloodSamples;
  const status = samples != null ? bloodWorkStatus(samples) : undefined;
  const badge = status ? BLOOD_STATUS_BADGE[status] : undefined;

  const goToAssignVials = useCallback(() => {
    goTo(paths.assignVials.url({ patientId, visitId }));
  }, [goTo, patientId, visitId]);

  return (
    <div className={styles.scanItem}>
      <div className={styles.itemTitle}>
        <Typography variant="title-s">Blood Work</Typography>
        <Link onPress={goToAssignVials}>
          {status === "complete" ? "Re-assign vials" : "Assign vials"}
        </Link>
      </div>
      {badge ? (
        <StatusBadge label={badge.label} highlight={badge.highlight} />
      ) : undefined}
    </div>
  );
}

function ScanStatusBadge({ scanState }: ScanStatusBadgeProps) {
  const { label, highlight } = useMemo<{
    label: string;
    highlight?: StatusBadgeHighlight;
  }>(() => {
    switch (scanState) {
      case APITypesV1.ImmutableScanState.Finished:
        return {
          label: "Completed",
          highlight: "success",
        };
      case APITypesV1.ImmutableScanState.Inprogress:
        return {
          label: "In progress",
          highlight: undefined,
        };
      case APITypesV1.ImmutableScanState.Available:
        return {
          label: "Ready",
          highlight: undefined,
        };
      case APITypesV1.ImmutableScanState.Failed:
        return {
          label: "Missing",
          highlight: "error",
        };
      default:
        return {
          label: "",
          highlight: undefined,
        };
    }
  }, [scanState]);

  return <StatusBadge label={label} highlight={highlight} />;
}

const BLOOD_STATUS_BADGE: Record<
  BloodWorkStatus,
  { label: string; highlight: StatusBadgeHighlight }
> = {
  complete: { label: "Completed", highlight: "success" },
  partial: { label: "Partially assigned", highlight: "warning" },
  missing: { label: "Missing", highlight: "error" },
};

function ScanTile({
  title,
  scanState,
  action,
}: {
  title: string;
  scanState?: APITypesV1.ImmutableScanState;
  action?: ReactNode;
}) {
  return (
    <div className={styles.scanItem}>
      <div className={styles.itemTitle}>
        <Typography variant="title-s">{title}</Typography>
        {action}
      </div>
      {scanState != null ? (
        <ScanStatusBadge scanState={scanState} />
      ) : undefined}
    </div>
  );
}
